// OMNI Design System Select Recipe for Chakra UI v3
// Based on OMNI CSS select and dropdown styles

import { defineSlotRecipe } from '@chakra-ui/react';

export const selectRecipe = defineSlotRecipe({
  className: 'omni-select',
  slots: ['root', 'trigger', 'content', 'item', 'itemText', 'itemIndicator'],
  base: {
    root: {
      position: 'relative',
      width: '100%',
    },

    trigger: {
      // Base styles similar to input
      appearance: 'none',
      borderRadius: 'base',
      border: '1px solid',
      borderColor: 'border.default',
      backgroundColor: 'bg.surface',
      color: 'fg.default',
      fontFamily: 'body',
      fontSize: 'md',
      fontWeight: 'normal',
      lineHeight: 'normal',
      outline: 'transparent',
      position: 'relative',
      transition: 'all 200ms ease',
      width: '100%',
      minWidth: '0',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',

      // Dropdown arrow styling
      _after: {
        content: '""',
        position: 'absolute',
        right: '3',
        width: '0',
        height: '0',
        borderLeft: '4px solid transparent',
        borderRight: '4px solid transparent',
        borderTop: '4px solid',
        borderTopColor: 'fg.muted',
        pointerEvents: 'none',
      },

      _hover: {
        borderColor: 'border.emphasis',

        _after: {
          borderTopColor: 'fg.default',
        },
      },

      _focus: {
        borderColor: 'brand.500',
        boxShadow: 'bulma-focus',
        outline: 'none',

        _after: {
          borderTopColor: 'brand.500',
        },
      },

      _disabled: {
        opacity: 0.6,
        cursor: 'not-allowed',
        backgroundColor: 'bg.muted',
        borderColor: 'border.subtle',

        _after: {
          borderTopColor: 'fg.subtle',
        },
      },

      _invalid: {
        borderColor: 'red.500',
        boxShadow: '0 0 0 0.125em rgba(223, 86, 120, 0.25)',
      },

      // Open state
      _open: {
        borderColor: 'brand.500',
        boxShadow: 'bulma-focus',

        _after: {
          transform: 'rotate(180deg)',
          borderTopColor: 'brand.500',
        },
      },
    },

    content: {
      // Dropdown content styling
      position: 'absolute',
      top: '100%',
      left: '0',
      right: '0',
      zIndex: 'dropdown',
      marginTop: '1',
      borderRadius: 'base',
      border: '1px solid',
      borderColor: 'border.default',
      backgroundColor: 'bg.surface',
      boxShadow: 'dropdown-tooltip', // OMNI dropdown shadow
      maxHeight: '60',
      overflowY: 'auto',
      padding: '1',

      // Animation
      opacity: 0,
      transform: 'translateY(-4px)',
      transition: 'all 150ms ease',

      _open: {
        opacity: 1,
        transform: 'translateY(0)',
      },
    },

    item: {
      display: 'flex',
      alignItems: 'center',
      padding: '2.5 3',
      borderRadius: 'sm',
      cursor: 'pointer',
      fontSize: 'md',
      fontWeight: 'normal',
      color: 'fg.default',
      transition: 'all 150ms ease',

      _hover: {
        backgroundColor: 'brand.50',
        color: 'brand.700',
      },

      _focus: {
        backgroundColor: 'brand.100',
        color: 'brand.800',
        outline: 'none',
      },

      _selected: {
        backgroundColor: 'brand.500',
        color: 'white',
        fontWeight: 'medium',
      },

      _disabled: {
        opacity: 0.5,
        cursor: 'not-allowed',

        _hover: {
          backgroundColor: 'transparent',
          color: 'fg.default',
        },
      },
    },

    itemText: {
      flex: 1,
      textAlign: 'left',
    },

    itemIndicator: {
      marginLeft: '2',
      fontSize: 'sm',
      color: 'currentColor',
      opacity: 0, // Hidden by default

      // Check mark or selected indicator
      _selected: {
        opacity: 1,
      },
    },
  },

  variants: {
    size: {
      xs: {
        trigger: {
          fontSize: '2xs',
          height: '6',
          paddingX: '2',
          paddingY: '1',

          _after: {
            right: '2',
            borderWidth: '3px 3px 0 3px',
          },
        },
        item: {
          fontSize: '2xs',
          padding: '1.5 2',
        },
      },

      sm: {
        trigger: {
          fontSize: 'xs',
          height: '8',
          paddingX: '3',
          paddingY: '2',

          _after: {
            right: '2.5',
            borderWidth: '3px 3px 0 3px',
          },
        },
        item: {
          fontSize: 'xs',
          padding: '2 2.5',
        },
      },

      md: {
        trigger: {
          fontSize: 'md',
          height: '10',
          paddingX: '4',
          paddingY: '2.5',

          _after: {
            right: '3',
            borderWidth: '4px 4px 0 4px',
          },
        },
        item: {
          fontSize: 'md',
          padding: '2.5 3',
        },
      },

      lg: {
        trigger: {
          fontSize: 'lg',
          height: '12',
          paddingX: '5',
          paddingY: '3',

          _after: {
            right: '4',
            borderWidth: '5px 5px 0 5px',
          },
        },
        item: {
          fontSize: 'lg',
          padding: '3 4',
        },
      },

      xl: {
        trigger: {
          fontSize: 'xl',
          height: '14',
          paddingX: '6',
          paddingY: '4',

          _after: {
            right: '5',
            borderWidth: '5px 5px 0 5px',
          },
        },
        item: {
          fontSize: 'xl',
          padding: '4 5',
        },
      },
    },

    variant: {
      outline: {
        trigger: {
          borderColor: 'border.default',
          backgroundColor: 'bg.surface',
        },
      },

      filled: {
        trigger: {
          borderColor: 'transparent',
          backgroundColor: 'bg.subtle',

          _hover: {
            backgroundColor: 'bg.muted',
          },

          _focus: {
            backgroundColor: 'bg.surface',
            borderColor: 'brand.500',
          },
        },
      },

      flushed: {
        trigger: {
          borderColor: 'transparent',
          backgroundColor: 'transparent',
          borderRadius: '0',
          borderBottomWidth: '2px',
          borderBottomColor: 'border.default',
          paddingX: '0',

          _focus: {
            borderBottomColor: 'brand.500',
            boxShadow: 'none',
          },

          _after: {
            right: '0',
          },
        },
      },

      unstyled: {
        trigger: {
          border: 'none',
          backgroundColor: 'transparent',
          borderRadius: '0',
          height: 'auto',
          paddingX: '0',
          paddingY: '0',

          _focus: {
            boxShadow: 'none',
          },
        },
      },
    },

    state: {
      success: {
        trigger: {
          borderColor: 'green.500',

          _focus: {
            borderColor: 'green.500',
            boxShadow: '0 0 0 0.125em rgba(44, 196, 173, 0.25)',
          },
        },
      },

      warning: {
        trigger: {
          borderColor: 'yellow.500',

          _focus: {
            borderColor: 'yellow.500',
            boxShadow: '0 0 0 0.125em rgba(254, 216, 130, 0.25)',
          },
        },
      },

      error: {
        trigger: {
          borderColor: 'red.500',

          _focus: {
            borderColor: 'red.500',
            boxShadow: '0 0 0 0.125em rgba(223, 86, 120, 0.25)',
          },
        },
      },
    },
  },

  compoundVariants: [
    {
      variant: 'filled',
      state: 'success',
      css: {
        trigger: {
          backgroundColor: 'green.50',
          borderColor: 'green.500',
        },
      },
    },

    {
      variant: 'filled',
      state: 'warning',
      css: {
        trigger: {
          backgroundColor: 'yellow.50',
          borderColor: 'yellow.500',
        },
      },
    },

    {
      variant: 'filled',
      state: 'error',
      css: {
        trigger: {
          backgroundColor: 'red.50',
          borderColor: 'red.500',
        },
      },
    },
  ],

  defaultVariants: {
    size: 'md',
    variant: 'outline',
  },
});
